import { useCallback, useRef } from "react"

/**
 * Ramener le parent à la section qu'il doit corriger.
 *
 * La section se mesure DANS le contenu défilant (`measureLayout` sur le nœud
 * intérieur du ScrollView) plutôt que par une addition d'`onLayout` relatifs à
 * des parents différents — l'addition visait trop court d'un bandeau. Les
 * `onLayout` restent la mesure de repli quand `measureLayout` n'est pas
 * disponible : l'appelant dit alors ce qu'il faut ajouter au-dessus
 * (`fallbackOffset`, l'espaceur du bandeau sur petit écran par exemple).
 */
export const useFunnelSectionScroll = (scrollRef) => {
    const sectionY = useRef({})
    const sectionRefs = useRef({})

    const sectionRef = useCallback(
        (key) => (node) => {
            sectionRefs.current[key] = node
        },
        []
    )
    const onSectionLayout = useCallback(
        (key) => (event) => {
            sectionY.current[key] = event?.nativeEvent?.layout?.y ?? 0
        },
        []
    )

    const scrollToSection = useCallback(
        (key, fallbackOffset = 0) => {
            const scrollToY = (y) =>
                scrollRef.current?.scrollTo({
                    y: Math.max(0, y - 8),
                    animated: true,
                })
            const fallbackY = fallbackOffset + (sectionY.current[key] ?? 0)
            const target = sectionRefs.current[key]
            const inner = scrollRef.current?.getInnerViewNode?.()
            if (target?.measureLayout && inner) {
                target.measureLayout(
                    inner,
                    (_x, y) => scrollToY(y),
                    () => scrollToY(fallbackY)
                )
            } else {
                scrollToY(fallbackY)
            }
        },
        [scrollRef]
    )

    return { sectionRef, onSectionLayout, sectionY, scrollToSection }
}
